import moment from "moment";

import VLFTextField from "@/components/Input/TextField";

import {AttachmentAPI} from "@/api/attachment";

import {
	REQUIRED_FIELD,
	externalValuationOptions,
	valuationOptions,
} from "@/utils/constants";

export const checkRequiredFields = (requiredFields, fields) => {
	const currentErrorsFields = [];
	requiredFields.forEach((rf) => {
		if (fields[rf] === null || fields[rf] === undefined) {
			currentErrorsFields.push(rf);
		}
	});

	return currentErrorsFields;
};

export const fetchData = async (
	func,
	filters,
	before,
	success,
	error,
	after,
) => {
	before && before();
	try {
		const response = await func(filters);
		success && success(response);
	} catch (e) {
		error && error();
	} finally {
		after && after();
	}
};

export const getTextField = ({
	fieldKey,
	type = "text",
	label,
	placeholder,
	required = false,
	editable = true,
	disabled = false,
	values,
	setValues,
	errors,
	setErrors,
}) => {
	return (
		<VLFTextField
			value={values?.[fieldKey] || ""}
			onChange={(e) => {
				required && setErrors && setErrors([]);
				setValues({...values, [fieldKey]: e.target.value});
			}}
			type={type}
			label={label}
			placeholder={placeholder || label}
			required={required}
			editable={editable}
			disabled={disabled}
			size="small"
			helperText={
				errors && errors?.indexOf(fieldKey) !== -1 ? REQUIRED_FIELD : ""
			}
			error={errors && errors?.indexOf(fieldKey) !== -1}
		/>
	);
};

export const getTextAreaField = ({
	fieldKey,
	label,
	placeholder,
	required,
	editable,
	minRows = 3,
	maxRows = 3,
	errors,
	setErrors,
	setValues,
	values,
}) => {
	return (
		<VLFTextField
			value={values[fieldKey] || ""}
			onChange={(e) => {
				required && setErrors && setErrors([]);
				setValues({...values, [fieldKey]: e.target.value});
			}}
			label={label}
			placeholder={placeholder || label}
			required={required}
			helperText={
				errors && errors.indexOf(fieldKey) !== -1 ? REQUIRED_FIELD : ""
			}
			error={errors && errors.indexOf(fieldKey) !== -1}
			size="small"
			multiline
			minRows={minRows}
			maxRows={maxRows}
			editable={editable}
		/>
	);
};

export const getCurrentPlayer = (players, item, withCategory = false) => {
	const currentPlayer = players?.filter((p) => p.id === item.playerId)?.[0];
	return currentPlayer
		? currentPlayer?.firstName +
				" " +
				currentPlayer?.lastName +
				(withCategory ? " (" + currentPlayer?.category + ")" : "")
		: "";
};

export const getCurrentCategory = (categories, item) => {
	const currentCategory = categories?.filter(
		(c) => (c.value || c.id) === item.playerCategoryId,
	)?.[0];
	return currentCategory ? currentCategory?.label || currentCategory?.name : "";
};

export const isCompetitiveActivity = (categories, category) => {
	const currentCategory = categories?.filter(
		(c) => (c.name || c.label) === category,
	)?.[0];
	return !currentCategory?.basicActivities;
};

const getHeightRange = (category) => {
	let minHeight,
		maxHeight = 0;

	switch (category) {
		case "Serie D":
		case "Primavera":
			minHeight = 188;
			maxHeight = 196;
			break;
		case "Juniores":
		case "Under 18":
			minHeight = 180;
			maxHeight = 196;
			break;
		case "Under 17":
			minHeight = 180;
			maxHeight = 196;
			break;
		case "Under 16":
			minHeight = 175;
			maxHeight = 190;
			break;
		case "Under 15":
			minHeight = 170;
			maxHeight = 185;
			break;
		case "Under 14":
			minHeight = 160;
			maxHeight = 180;
			break;
		case "Under 13":
			minHeight = 160;
			maxHeight = 178;
			break;
		case "Under 12":
			minHeight = 150;
			maxHeight = 170;
			break;
		case "Under 11":
			minHeight = 145;
			maxHeight = 165;
			break;
		case "Under 10":
			minHeight = 140;
			maxHeight = 160;
			break;
		case "Under 09":
			minHeight = 130;
			maxHeight = 150;
			break;
		case "Under 08":
			minHeight = 120;
			maxHeight = 140;
			break;
		default:
			break;
	}

	return {minHeight, maxHeight};
};
const getWeightRange = (category) => {
	let minWeight,
		maxWeight = 0;

	switch (category) {
		case "Primavera":
			minWeight = 75;
			maxWeight = 85;
			break;
		case "Under 18":
			minWeight = 72;
			maxWeight = 82;
			break;
		case "Under 17":
			minWeight = 68;
			maxWeight = 78;
			break;
		case "Under 16":
			minWeight = 62;
			maxWeight = 72;
			break;
		case "Under 15":
			minWeight = 55;
			maxWeight = 65;
			break;
		case "Under 14":
			minWeight = 46;
			maxWeight = 58;
			break;
		case "Under 13":
			minWeight = 40;
			maxWeight = 50;
			break;
		case "Under 12":
			minWeight = 32;
			maxWeight = 42;
			break;
		case "Under 11":
			minWeight = 27;
			maxWeight = 37;
			break;
		case "Under 10":
			minWeight = 25;
			maxWeight = 35;
			break;
		case "Under 09":
			minWeight = 23;
			maxWeight = 33;
			break;
		case "Under 08":
			minWeight = 20;
			maxWeight = 30;
			break;
		default:
			break;
	}

	return {minWeight, maxWeight};
};

export const getPlayerStructure = (categoria, altezza, peso) => {
	const {minHeight, maxHeight} = getHeightRange(categoria.toString());
	// const {minWeight, maxWeight} = getWeightRange(categoria.toString());

	const altezzaNormalizzata = (altezza - minHeight) / (maxHeight - minHeight);
	// const pesoNormalizzato = (peso - minWeight) / (maxWeight - minWeight);

	const strutturaNormalizzata = altezzaNormalizzata; // 0.6 * altezzaNormalizzata + 0.4 * pesoNormalizzato;

	const strutturaFinale = Math.round(strutturaNormalizzata * 10);

	return Math.max(1, Math.min(10, strutturaFinale));
};

export const isDateValid = (date) => {
	return date && moment(date).isValid() && moment(date).year() > 2000;
};

export const getNestedValue = (obj, path) => {
	return path?.split(".").reduce((acc, key) => acc && acc[key], obj);
};

export const orderByDate = (data, dateKey, order = "ascend") => {
	return data.sort((a, b) => {
		const dateA = new Date(getNestedValue(a, dateKey));
		const dateB = new Date(getNestedValue(b, dateKey));

		return order === "ascend" ? dateA - dateB : dateB - dateA;
	});
};

export const getAttachment = async (id, list) => {
	try {
		const currentFile = list.filter((a) => a.id === id)?.[0];
		const response = await AttachmentAPI.getById(id, {
			responseType: "blob",
		});

		const extension = currentFile.filePath
			.split("\\")
			.pop()
			.split(".")
			.pop()
			.toLowerCase();
		let mimeType = "application/octet-stream";

		switch (extension) {
			case "pdf":
				mimeType = "application/pdf";
				break;
			case "png":
				mimeType = "image/png";
				break;
			case "jpg":
			case "jpeg":
				mimeType = "image/jpeg";
				break;
			case "doc":
				mimeType = "doc";
				break;
			default:
				break;
		}

		const blob = new Blob([response], {type: mimeType});

		const url = window.URL.createObjectURL(blob);

		const link = document.createElement("a");
		link.href = url;
		link.setAttribute("download", currentFile.filePath.split("\\").pop());

		document.body.appendChild(link);
		link.click();

		document.body.removeChild(link);
	} catch (error) {
		console.log(error);
	}
};

export const getAlertSeverity = (valuation, isScoutReport = true) => {
	const options = isScoutReport ? valuationOptions : externalValuationOptions;
	const message = options?.filter((o) => o.value === valuation?.toString())?.[0]
		?.label;

	switch (valuation) {
		case 0:
			return {severity: "success", message, value: "Idoneo"};
		case 3:
		case 1:
			return {severity: "error", message, value: "Non Idoneo"};
		case 2:
			return {
				severity: "warning",
				message,
				value: "Da seguire",
			};
		case -1:
		default:
			return {severity: "info", message: "---", value: "N/A"};
	}
};

export const stdev = (arr) => {
	if (!arr || !arr.length) {
		return 0;
	}
	let mean =
		arr.reduce((acc, curr) => {
			return acc + curr;
		}, 0) / arr.length;

	arr = arr.map((k) => {
		return (k - mean) ** 2;
	});

	let sum = arr?.reduce((acc, curr) => acc + curr, 0);

	return +parseFloat(Math.sqrt(sum / arr.length)).toFixed(2);
};

export const getValuationType = (type) => {
	switch (type) {
		case undefined:
			return "Valutazione scout";
		case 2:
		case 4:
			return "Valutazione tecnica";
		case 3:
		case 5:
			return "Provino";
		case 6:
			return "Valutazione dirigente";
		default:
			return "";
	}
};

export const getBirthYears = (start = 1995, end = 2020) => {
	const birthYears = [];
	for (let i = start; i <= end; i++) {
		birthYears.push({value: i, label: i.toString()});
	}

	return birthYears;
};
